/**
 * 인증 관련 유틸리티 함수
 * 토큰 만료 체크, 에러 타입 정의 등
 */

/**
 * 인증 에러 타입
 */
export type AuthErrorType =
  | 'RefreshAccessTokenError'
  | 'NoRefreshToken'
  | 'SessionExpired'
  | 'Unauthorized';

/**
 * 토큰 만료 여부 확인
 * @param expiresAt 만료 시간 (밀리초 타임스탬프)
 * @returns 만료 여부
 */
export function isTokenExpired(expiresAt: number | undefined): boolean {
  if (!expiresAt) return true;
  return Date.now() >= expiresAt;
}

/**
 * 토큰 갱신이 필요한지 확인 (버퍼 시간 포함)
 * @param expiresAt 만료 시간 (밀리초 타임스탬프)
 * @param bufferMs 버퍼 시간 (밀리초, 기본 30초)
 * @returns 갱신 필요 여부
 */
export function shouldRefreshToken(
  expiresAt: number | undefined,
  bufferMs: number = 30 * 1000,
): boolean {
  if (!expiresAt) return true;
  return Date.now() >= expiresAt - bufferMs;
}

/**
 * 토큰 남은 시간 계산 (초 단위)
 * @param expiresAt 만료 시간 (밀리초 타임스탬프)
 * @returns 남은 시간 (초), 만료된 경우 0
 */
export function getTokenRemainingTime(expiresAt: number | undefined): number {
  if (!expiresAt) return 0;
  const remaining = Math.floor((expiresAt - Date.now()) / 1000);
  return remaining > 0 ? remaining : 0;
}

/**
 * 세션 에러인지 확인
 * @param error 에러 문자열
 * @returns 세션 에러 여부
 */
export function isSessionError(error: string | undefined): boolean {
  if (!error) return false;
  return (
    error === 'RefreshAccessTokenError' ||
    error === 'NoRefreshToken' ||
    error === 'SessionExpired'
  );
}

/**
 * 에러 메시지 반환
 * @param error 에러 타입
 * @returns 사용자 친화적 에러 메시지
 */
export function getAuthErrorMessage(error: AuthErrorType | string): string {
  switch (error) {
    case 'RefreshAccessTokenError':
      return '세션이 만료되었습니다. 다시 로그인해주세요.';
    case 'NoRefreshToken':
      return '인증 정보가 없습니다. 다시 로그인해주세요.';
    case 'SessionExpired':
      return '세션이 만료되었습니다. 다시 로그인해주세요.';
    case 'Unauthorized':
      return '인증이 필요합니다. 로그인해주세요.';
    default:
      return '인증 오류가 발생했습니다. 다시 로그인해주세요.';
  }
}

/**
 * 세션 갱신 간격 (밀리초)
 * NextAuth SessionProvider의 refetchInterval에 사용
 * Access Token 만료 시간(5분)보다 짧게 설정
 */
export const SESSION_REFETCH_INTERVAL = 4 * 60 * 1000; // 4분

/**
 * 토큰 갱신 버퍼 시간 (밀리초)
 * 만료 전 이 시간만큼 미리 갱신
 *
 * 중요: SessionProvider refetchInterval(4분)과 Access Token 만료(5분)의
 * 관계에서, refetch 시점의 남은 시간(60초)보다 반드시 커야 함.
 * buffer > (tokenLifetime % refetchInterval) 이어야 갱신이 제때 발생.
 */
export const TOKEN_REFRESH_BUFFER = 90 * 1000; // 90초
